import React from 'react';
import ReactSwitch from 'react-switch';
import { LoiComparisonFormDto } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import { NonCompetitionRestriction, NonSolicitationRestriction } from '../../../../slices/marketplace/loi-form/term-form';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import {
    getFieldComment,
    formatCell,
    PRICE_ROWS,
} from './LoiComparisonRows';
import { SfasFeeParams } from '../../../../slices/marketplace/loi-comparison/consideration-fees';
import { isLoiProcessPostedToSeller } from '../../../../slices/marketplace/loi-comparison/models/loi-forms-process.model';
import {
    ConsiderationSummaryTable,
    TAIL_COLUMN_HEADERS,
    DETAIL_TAIL_COLUMN_HEADERS,
    COLLAPSIBLE_COLUMN_TOKEN,
} from './ConsiderationSummaryTable';

export { PRICE_ROWS, TAIL_COLUMN_HEADERS, DETAIL_TAIL_COLUMN_HEADERS, COLLAPSIBLE_COLUMN_TOKEN };

export type CovenantRowKind = 'subheader' | 'group' | 'value';

export interface CovenantRow {
    kind: CovenantRowKind;
    label: string;
    source?: 'nonComp' | 'nonSolicit';
    key?: string;
}

export const COVENANT_ROWS: CovenantRow[] = [
    { kind: 'subheader', label: 'Non-Competition Restrictions' },
    { kind: 'value', label: 'Distance: Radius (miles)', source: 'nonComp', key: 'distanceRadiusMiles' },
    { kind: 'value', label: 'Duration: Months from Closing', source: 'nonComp', key: 'durationMonthsFromClosing' },
    { kind: 'value', label: 'Duration: Months from Employment', source: 'nonComp', key: 'durationMonthsFromEmployment' },
    { kind: 'subheader', label: 'Non-Solicitation Restrictions' },
    { kind: 'group', label: 'Employees' },
    { kind: 'value', label: 'Term, months', source: 'nonSolicit', key: 'employeesTerm' },
    { kind: 'value', label: 'Tail, months', source: 'nonSolicit', key: 'employeesTail' },
    { kind: 'group', label: 'Customers' },
    { kind: 'value', label: 'Term, months', source: 'nonSolicit', key: 'customersTerm' },
    { kind: 'value', label: 'Tail, months', source: 'nonSolicit', key: 'customersTail' },
];

const NON_COMP_COMMENT_ROW_COUNT = 3;
const NON_SOLICIT_COMMENT_ROW_COUNT = 4;

export interface BuyerSummaryCardProps {
    item: LoiComparisonFormDto;
    levelOfService: number;
    sfasParams?: SfasFeeParams | null;
    showFairValue: boolean;
    collapsibleColumns: Record<string, boolean>;
    onToggleColumn: (key: string) => void;
    onBuyerClick?: () => void;
    isDeepDive: boolean;
    selectedRowLabel: string | null;
    onCellsClick: (rowLabel: string, buyerId: number) => void;
    isEventCompleted: boolean;
    canSelectThisBuyer: boolean;
    onSelectClick: () => void;
    onResubmissionToggle: (buyerId: number) => void;
    onViewHistory?: () => void;
    tooltipIdSuffix?: string;
    isBroker?: boolean;
    onApproveClick?: () => void;
    onDeclineClick?: () => void;
    isDetailView?: boolean;
}

export const BuyerSummaryCard = ({
    item,
    levelOfService,
    sfasParams = null,
    showFairValue,
    collapsibleColumns,
    onToggleColumn,
    onBuyerClick,
    isDeepDive,
    selectedRowLabel,
    onCellsClick,
    isEventCompleted,
    canSelectThisBuyer,
    onSelectClick,
    onResubmissionToggle,
    onViewHistory,
    tooltipIdSuffix = '',
    isBroker,
    onApproveClick,
    onDeclineClick,
    isDetailView = false,
}: BuyerSummaryCardProps) => {
    const postedProcesses = item.loiFormsProcesses.filter(isLoiProcessPostedToSeller);

    const renderBuyerGroupHeaderRow = (colSpan: number) => (
        <tr>
            <th colSpan={colSpan} className="buyer-group-header">
                <div className="buyer-group-header__row">
                    {onBuyerClick ? (
                        <span className="clickable-text" onClick={onBuyerClick}>
                            {item.buyerName}
                        </span>
                    ) : (
                        <span>{item.buyerName}</span>
                    )}
                    {postedProcesses.length > 1 && onViewHistory && (
                        <>
                            <span className="buyer-group-header__separator">—</span>
                            <button
                                className="loi-view-history-btn"
                                onClick={onViewHistory}>
                                View History
                            </button>
                        </>
                    )}
                    {item.isSelected && !isDetailView && (
                        <span className="loi-selected-badge" aria-label="Selected LOI">
                            <span className="loi-selected-badge-check">✓</span>
                            Selected LOI
                        </span>
                    )}
                    {item.isAwaitingAdvisorApproval && (
                        <span className="buyer-summary-card__selected-marker text-warning">
                            Waiting For Approval
                        </span>
                    )}
                </div>
            </th>
        </tr>
    );

    const renderResubmissionToggleRow = (colSpan: number) => (
        item.isResubmissionToggleVisible && !isDeepDive && !isDetailView ? (
            <tr className="resubmission-toggle-row">
                <td colSpan={colSpan}>
                    <div className="resubmission-toggle-content">
                        <span className="resubmission-toggle-label">Enable Resubmission of Offer</span>
                        <ReactSwitch
                            offColor="#d92550"
                            checked={item.isResubmissionEnabled}
                            onChange={item.canToggleResubmission && !item.isResubmissionEnabled
                                ? () => onResubmissionToggle(item.buyerId)
                                : () => {}}
                            disabled={!item.canToggleResubmission || item.isResubmissionEnabled}
                            height={22}
                        />
                    </div>
                </td>
            </tr>
        ) : null
    );

    return (
        <div className={`buyer-summary-card${item.isSelected && !isDetailView ? ' buyer-summary-card--selected' : ''}`}>
            <ConsiderationSummaryTable
                item={item}
                levelOfService={levelOfService}
                sfasParams={sfasParams}
                showFairValue={showFairValue}
                showLiabilityPayoffs={isDetailView}
                tooltipIdSuffix={tooltipIdSuffix}
                collapsibleColumns={collapsibleColumns}
                onToggleColumn={onToggleColumn}
                isDeepDive={isDeepDive}
                selectedRowLabel={selectedRowLabel}
                onCellsClick={onCellsClick}
                headerRow={renderBuyerGroupHeaderRow}
                footerRow={renderResubmissionToggleRow}
            />

            {!isDeepDive && (
                <>
                    {canSelectThisBuyer && !item.isSelected && !isDetailView && (
                        <div className="buyer-summary-card__action-row">
                            <button
                                type="button"
                                className="btn btn-primary buyer-summary-card__select-btn"
                                onClick={onSelectClick}
                            >
                                Select &amp; Execute LOI
                            </button>
                        </div>
                    )}
                    {isBroker && item.isAwaitingAdvisorApproval && (
                        <div className="buyer-summary-card__action-row">
                            <button
                                type="button"
                                className="btn btn-primary buyer-summary-card__select-btn"
                                onClick={onApproveClick}
                            >
                                Approve Selection
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-danger buyer-summary-card__select-btn"
                                onClick={onDeclineClick}
                            >
                                Decline Selection
                            </button>
                        </div>
                    )}
                </>
            )}

        </div>
    );
};

export interface CovenantsValueColumnProps {
    item: LoiComparisonFormDto;
    tooltipIdSuffix?: string;
    onBuyerClick?: () => void;
    onViewHistory?: () => void;
}

export const CovenantsValueColumn = ({ item, tooltipIdSuffix = '', onBuyerClick, onViewHistory }: CovenantsValueColumnProps) => {
    const nonComp = item.form?.termForm?.nonCompetitionRestriction;
    const nonSolicit = item.form?.termForm?.nonSolicitationRestriction;
    const postedProcesses = item.loiFormsProcesses.filter(isLoiProcessPostedToSeller);

    const covenantComments = [
        ...Array.from({ length: NON_COMP_COMMENT_ROW_COUNT }, (_, i) => getFieldComment(item, `ncr:${i}`)),
        ...Array.from({ length: NON_SOLICIT_COMMENT_ROW_COUNT }, (_, i) => getFieldComment(item, `nsr:${i}`)),
    ].filter(Boolean);
    const covenantComment = covenantComments.length > 0 ? covenantComments.join('\n') : undefined;

    const getCovenantValue = (row: CovenantRow) => {
        if (row.source === 'nonComp') {
            return nonComp?.[row.key as keyof NonCompetitionRestriction];
        }
        if (row.source === 'nonSolicit') {
            return nonSolicit?.[row.key as keyof NonSolicitationRestriction];
        }
        return undefined;
    };

    return (
        <table>
            <thead>
                <tr>
                    <th className="buyer-group-header">
                        <div className="buyer-group-header__row">
                            {onBuyerClick ? (
                                <span className="clickable-text" onClick={onBuyerClick}>
                                    {item.buyerName}
                                </span>
                            ) : (
                                <span>{item.buyerName}</span>
                            )}
                            {postedProcesses.length > 1 && onViewHistory && (
                                <>
                                    <span className="buyer-group-header__separator">—</span>
                                    <button
                                        className="loi-view-history-btn"
                                        onClick={onViewHistory}>
                                        View History
                                    </button>
                                </>
                            )}
                            {covenantComment && (
                                <InfoTooltip
                                    text={covenantComment}
                                    idText={`covenant-comment-${item.buyerId}${tooltipIdSuffix}`}
                                />
                            )}
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody>
                {COVENANT_ROWS.map((row, idx) => (
                    <tr key={`${row.kind}-${row.label}-${idx}`}>
                        {row.kind === 'value' ? (
                            <td className="cell-value covenant-value">{formatCell(getCovenantValue(row))}</td>
                        ) : (
                            <td />
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
